import { ComponentProps, FC } from 'react'
import { useUnmount } from 'ahooks'

import { Tabs } from '@trustycars/genie-ui/components'

import ExtraIcon from '$icons/ExtraIcon'

import { authMethods } from '$constants/auth'

import useEmailLogin from '$actions/useEmailLogin'
import useAppDetail from '$actions/useAppDetail'
import useSignal from '$actions/useSignal'

import LoginForm from '$hook-forms/LoginForm'
import MobileOTPRequestForm from '$hook-forms/MobileOTPRequestForm'
import useMobileOTPRequest from '$actions/useMobileOTPRequest'
import usePasskeyLogin from '$actions/usePasskeyLogin'

import MFABlock from '$blocks/MFABlock'
import Link from '$elements/Link'
import AppInfo from '$elements/AppInfo'

import SeparatorWithLabel from '$elements/SeparatorWithLabel'

import session from '$store/session'
import queryParams from '$store/queryParams'

import {
  appInfo,
  container,
  forgotLink,
  header,
  tabListStyle,
  tabListTriggerStyle,
  title,
  secondaryLoginAction,
  secondaryLoginActionsContainer,
  separator,
  tabContent
} from './styles.css'

const SessionLink: FC = () => {
  const { tokenDetails } = useSignal(session)

  const hasTokens = !!tokenDetails.length

  if (!hasTokens) return null

  return (
    <Link
      to="authAccounts"
      data-testid="link-use-existing-sessions"
      options={({ queryParams }) => ({ queryParams })}
      className={secondaryLoginAction}
    >
      <ExtraIcon name="exit-door" />
      Sign in with existing accounts
    </Link>
  )
}

const ForgotLink: FC = () => (
  <Link
    to="authForgotPassword"
    options={({ queryParams }) => ({ queryParams })}
    className={forgotLink}
  >
    Forgot password?
  </Link>
)

const LoginWithPasskeyButton: FC<ComponentProps<'button'>> = ({ ...props }) => (
  <button
    name="loginWithPasskey"
    type="button"
    className={secondaryLoginAction}
    {...props}
  >
    <ExtraIcon name="personal-key" />
    Sign in with passkey
  </button>
)

const SecondaryLoginActions: FC<{
  passkeyButtonProps: ComponentProps<typeof LoginWithPasskeyButton>
}> = ({ passkeyButtonProps }) => (
  <>
    <SeparatorWithLabel label="OR" containerProps={{ className: separator }} />

    <div className={secondaryLoginActionsContainer}>
      <SessionLink />
      <LoginWithPasskeyButton {...passkeyButtonProps} />
    </div>
  </>
)

export const LoginBlock: FC = () => {
  const {
    params: { client_id, auth_method }
  } = useSignal(queryParams)

  const targetApp = useAppDetail(client_id)
  const isAppTarget = !!client_id
  const isAppInfoLoading = isAppTarget && targetApp.loading

  // TODO: Revert this with the following component after launchpad API is working properly
  const isAppLogin = false
  // const isAppLogin = isAppInfoLoading || !!targetApp.data

  const appTitle = targetApp.data?.data.name
  const appLogo = targetApp.data?.data.logo
  const appDomain = targetApp.data?.data.domain

  const loginActions = useEmailLogin()
  const otpActions = useMobileOTPRequest()
  const passkeyLoginActions = usePasskeyLogin()

  useUnmount(() => {
    /**
     * Clear passkey state when leaving this page.
     * This stops any browser passkey popups from staying open
     * when the user goes to another page.
     */
    passkeyLoginActions.onReset()
  })

  return (
    <section className={container} data-testid="login-block">
      <header className={header}>
        <h2 className={title}>Sign in to your account</h2>
      </header>

      {isAppLogin && (
        <AppInfo
          isLoading={isAppInfoLoading}
          domain={appDomain}
          logo={appLogo}
          title={appTitle}
          containerProps={{ className: appInfo }}
        />
      )}

      <Tabs.Root defaultValue={auth_method} format={{ type: 'segmented' }}>
        <Tabs.List
          styleVariants={{ hAlign: 'center' }}
          className={tabListStyle}
        >
          <Tabs.Trigger value={authMethods[0]} className={tabListTriggerStyle}>
            Email
          </Tabs.Trigger>
          <Tabs.Trigger value={authMethods[1]} className={tabListTriggerStyle}>
            Phone Number
          </Tabs.Trigger>
        </Tabs.List>

        <Tabs.Content
          value={authMethods[0]}
          forceMount
          data-testid={`tab-${authMethods[0]}`}
          className={tabContent}
        >
          <LoginForm {...loginActions.loginForm} postFooter={<ForgotLink />} />

          <SecondaryLoginActions passkeyButtonProps={passkeyLoginActions} />
        </Tabs.Content>

        <Tabs.Content
          value={authMethods[1]}
          forceMount
          data-testid={`tab-${authMethods[1]}`}
          className={tabContent}
        >
          <MobileOTPRequestForm {...otpActions} />

          <SecondaryLoginActions passkeyButtonProps={passkeyLoginActions} />
        </Tabs.Content>
      </Tabs.Root>

      <MFABlock {...loginActions.mfaBlock} />
    </section>
  )
}

export default LoginBlock
